import { memo, useCallback, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../../constants/svgIcon';
import { getWebBoosterVairant } from '../../../../constants/webBoosterType';
import { AlbumPartition } from '../../../../models/SpaceStore';
import { Profile, WebBoosterType } from '../../../../models/UserStore';
import {
  AlbumCell,
  AlbumCellProps,
  AppBar,
  AvatarNode,
  FolderBreadcrumbRow,
  FolderBreadcrumbVariant,
  LoadMoreSpinner,
  ScrollArrows,
  SvgIcon,
} from '../../../Molecules';

import styles from './HomeClubTemplate.module.css';

export type HomeClubTemplateProps = {
  userProfile: Profile;
  webBoosterType: WebBoosterType | undefined;
  breadcrumbs: AlbumPartition[];
  hasRecentAlbum: boolean;
  albumData: AlbumCellProps[];
  isLoading: boolean;
  hasCursor: boolean;
  isJustFinishedCreate: boolean;
  onClickHome: () => void;
  onClickSwitchBooster: () => void;
  onClickCreateGames: () => void;
  onClickRecentAlbum: () => void;
  onClickBreadcrumb: () => void;
  loadMoreItems: () => void;
  onDefaultFinishCreation: () => void;
};

export const HomeClubTemplate = memo(
  ({
    userProfile,
    webBoosterType,
    breadcrumbs,
    hasRecentAlbum,
    albumData,
    isLoading,
    hasCursor,
    isJustFinishedCreate,
    onClickHome,
    onClickSwitchBooster,
    onClickCreateGames,
    onClickRecentAlbum,
    onClickBreadcrumb,
    loadMoreItems,
    onDefaultFinishCreation,
  }: HomeClubTemplateProps) => {
    const { t } = useTranslation();
    const albumListRef = useRef<HTMLDivElement | null>(null);

    const scrollToFirstGameSet = useCallback(() => {
      const albumListView = albumListRef.current;
      if (albumListView) {
        const elementRect = albumListView.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - window.innerHeight / 2;
        window.scrollTo({
          top: middle,
          behavior: 'smooth',
        });
      } else {
        window.scrollTo({
          top: 300,
          behavior: 'smooth',
        });
      }
    }, []);

    useEffect(() => {
      if (isJustFinishedCreate) {
        scrollToFirstGameSet();
        onDefaultFinishCreation();
      }
    }, [isJustFinishedCreate, onDefaultFinishCreation, scrollToFirstGameSet]);

    return (
      <div className={styles.homeClub}>
        <div className={styles.centeredContainer}>
          <ScrollArrows />
          <AppBar
            appBarClassName={styles.appbar}
            onClickBack={onClickHome}
            leading={
              <div className={styles.titleContainer}>
                <AvatarNode profile={userProfile} size={40} />
                <div className={styles.headerContainer}>
                  <div className={styles.spaceName}>{userProfile.name}</div>
                  <div
                    className={styles.boosterButton}
                    onClick={onClickSwitchBooster}
                  >
                    <div className={styles.boosterText}>
                      {t(getWebBoosterVairant(webBoosterType).name)}
                    </div>
                    <SvgIcon
                      name={SVGIconType.Triangle}
                      width={19}
                      height={19}
                      color={'white'}
                    />
                  </div>
                </div>
              </div>
            }
            withBack={true}
            height={67}
          />

          <div className={styles.orangeContainer}>
            <p className={styles.orangeText}>
              {t(getWebBoosterVairant(webBoosterType).neverForgetText)}
            </p>
            <div className={styles.textBoxCreate} onClick={onClickCreateGames}>
              {t(getWebBoosterVairant(webBoosterType).textToPractice)}
            </div>
          </div>

          <div className={styles.folderBreadcrumbRow}>
            <div>{t('Path: ')}</div>
            <FolderBreadcrumbRow
              webBoosterType={webBoosterType}
              breadcrumbs={breadcrumbs}
              variant={FolderBreadcrumbVariant.Black}
              onClickBreadcrumb={onClickBreadcrumb}
            />
          </div>

          {isLoading ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '1rem',
              }}
            >
              <Spinner
                animation="border"
                role="status"
                style={{ color: '#05656B' }}
              >
                <span className="visually-hidden">{t('Loading...')}</span>
              </Spinner>
            </div>
          ) : albumData.length === 0 ? (
            <>
              <div className={styles.myJourneys}>
                {/* <div className={'sectionTitle'}>{t('My Journeys')}</div> */}

                <div className={styles.emptyContainer}>
                  <SvgIcon
                    name={SVGIconType.PracticeTick}
                    className={styles.emptyAlbumIcon}
                    width={80}
                    height={80}
                    color={'#333333'}
                  />
                  <div className={styles.emptyAlbumText}>
                    {t('No Memory Practice Yet')}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {hasRecentAlbum ? (
                <div className={styles.whiteBarContainer}>
                  <div className={styles.whiteBar} onClick={onClickRecentAlbum}>
                    <SvgIcon
                      name={SVGIconType.RecentArrow}
                      width={28}
                      height={28}
                      color={'#333'}
                    />
                    <div className={styles.whiteBarText}>
                      {t('Recently Played')}
                    </div>
                    <div className={styles.whiteBarArrow}>
                      <SvgIcon
                        name={SVGIconType.Arrow}
                        width={20}
                        height={20}
                        color="#808080"
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              <div className={styles.myJourneys}>
                {/* <div className={'sectionTitle'}>{t('My Journeys')}</div> */}

                <div className={styles.albumList} ref={albumListRef}>
                  {albumData.map((item, index) => (
                    <div
                      key={index}
                      className={`${
                        isJustFinishedCreate && index === 0
                          ? styles.blinkAnimation
                          : ''
                      }`}
                    >
                      <AlbumCell
                        {...item}
                        classroomOwnerProfile={userProfile}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {hasCursor && (
                <LoadMoreSpinner
                  hasCursor={hasCursor}
                  loadMoreItems={loadMoreItems}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  },
);
